/**
 * Mise en forme facon WhatsApp (*gras*, _italique_, ~barre~, ```monospace```) appliquee a
 * l'affichage : le texte enregistre reste celui saisi par l'utilisateur.
 */

export type WhatsappStyle = 'bold' | 'italic' | 'strike' | 'code';

export type WhatsappNode = string | { style: WhatsappStyle; children: WhatsappNode[] };

const DELIMITERS: { marker: string; style: WhatsappStyle }[] = [
  // Le triple accent grave d'abord, pour ne pas le confondre avec un autre delimiteur.
  { marker: '```', style: 'code' },
  { marker: '*', style: 'bold' },
  { marker: '_', style: 'italic' },
  { marker: '~', style: 'strike' },
];

// Zones a ne jamais transformer : liens (ex. voiture_9.html) et @mentions @[Nom](id).
const PROTECTED_REGEX = /https?:\/\/\S+|www\.\S+|@\[[^\]]+\]\([^)]+\)/g;

const WORD_CHAR_REGEX = /[\p{L}\p{N}]/u;
const WHITESPACE_REGEX = /\s/;

const isWordChar = (char: string | undefined) => char !== undefined && WORD_CHAR_REGEX.test(char);
const isWhitespace = (char: string | undefined) =>
  char !== undefined && WHITESPACE_REGEX.test(char);

function findProtectedRanges(text: string): [number, number][] {
  const ranges: [number, number][] = [];
  for (const match of text.matchAll(PROTECTED_REGEX)) {
    const start = match.index ?? 0;
    ranges.push([start, start + match[0].length]);
  }
  return ranges;
}

/** Ouvrant : en debut de texte ou apres un espace / une ponctuation, suivi d'un non-blanc. */
function canOpen(text: string, index: number, marker: string) {
  const next = text[index + marker.length];
  return !isWordChar(text[index - 1]) && next !== undefined && !isWhitespace(next);
}

/** Fermant : precede d'un non-blanc, suivi d'une fin de texte / d'un espace / d'une ponctuation. */
function canClose(text: string, index: number, marker: string) {
  return !isWhitespace(text[index - 1]) && !isWordChar(text[index + marker.length]);
}

/**
 * Decoupe un texte en morceaux bruts et en morceaux mis en forme. Un texte sans mise en
 * forme ressort en un seul morceau, identique.
 */
export function parseWhatsappFormatting(text: string): WhatsappNode[] {
  const protectedRanges = findProtectedRanges(text);
  const protectedEnd = (index: number) =>
    protectedRanges.find(([start, end]) => index >= start && index < end)?.[1];

  const nodes: WhatsappNode[] = [];
  let plainStart = 0;
  let index = 0;

  while (index < text.length) {
    const skipTo = protectedEnd(index);
    if (skipTo !== undefined) {
      index = skipTo;
      continue;
    }

    const delimiter = DELIMITERS.find(({ marker }) => text.startsWith(marker, index));
    const closeIndex =
      delimiter && canOpen(text, index, delimiter.marker)
        ? findClosing(text, index + delimiter.marker.length, delimiter.marker, protectedEnd)
        : undefined;

    if (!delimiter || closeIndex === undefined) {
      index += 1;
      continue;
    }

    if (index > plainStart) nodes.push(text.slice(plainStart, index));

    const inner = text.slice(index + delimiter.marker.length, closeIndex);
    nodes.push({
      style: delimiter.style,
      // Pas de mise en forme a l'interieur d'un bloc monospace, comme dans WhatsApp.
      children: delimiter.style === 'code' ? [inner] : parseWhatsappFormatting(inner),
    });

    index = closeIndex + delimiter.marker.length;
    plainStart = index;
  }

  if (plainStart < text.length) nodes.push(text.slice(plainStart));

  return nodes;
}

function findClosing(
  text: string,
  from: number,
  marker: string,
  protectedEnd: (index: number) => number | undefined
): number | undefined {
  // Jamais a cheval sur deux lignes.
  const lineEnd = text.indexOf('\n', from);
  const limit = lineEnd === -1 ? text.length : lineEnd;

  let index = from + 1;
  while (index < limit) {
    const skipTo = protectedEnd(index);
    if (skipTo !== undefined) {
      index = skipTo;
      continue;
    }
    if (text.startsWith(marker, index) && canClose(text, index, marker)) return index;
    index += 1;
  }
  return undefined;
}

/** Texte sans les delimiteurs, pour les apercus qui n'affichent pas de mise en forme. */
export function stripWhatsappFormatting(text: string): string {
  const flatten = (nodes: WhatsappNode[]): string =>
    nodes.map(node => (typeof node === 'string' ? node : flatten(node.children))).join('');
  return flatten(parseWhatsappFormatting(text));
}
